import React from "react";
import { Star, Users, Award, ShieldCheck, ThumbsUp, Palette } from "lucide-react";
import { BrandIcon } from "../utils/icons";
import { siteConfig } from "../siteConfig";
import Reveal from "./Reveal";

const { about, colors, social = {} } = siteConfig;

const ICON_MAP = { Users, Award, ShieldCheck, ThumbsUp, Palette };

/* ── Inline social icons ── */
const GoogleBadge = () => (
  <a href={social.google || "#"} target="_blank" rel="noreferrer"
    className="w-11 h-11 rounded-full border-2 border-gray-200 flex items-center justify-center hover:scale-105 transition-transform bg-white shadow-sm">
    <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
  </a>
);

const FacebookBadge = () => (
  social.facebook ? (
    <a href={social.facebook} target="_blank" rel="noreferrer"
      className="w-11 h-11 rounded-full flex items-center justify-center hover:scale-105 transition-transform shadow-sm"
      style={{ backgroundColor: "#1877F2" }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect width="24" height="24" rx="12" fill="#1877F2"/><path d="M16 8h-2a1 1 0 0 0-1 1v2h3l-.5 3H13v7h-3v-7H8v-3h2V9a4 4 0 0 1 4-4h2v3z" fill="white"/></svg>
    </a>
  ) : null
);

const InstagramBadge = () => (
  social.instagram ? (
    <a href={social.instagram} target="_blank" rel="noreferrer"
      className="w-11 h-11 rounded-full flex items-center justify-center hover:scale-105 transition-transform shadow-sm"
      style={{ background: "linear-gradient(135deg,#f9a825,#e91e63,#9c27b0)" }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="2" y="2" width="20" height="20" rx="5.5" fill="url(#ig2)"/><circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.8"/><circle cx="17.2" cy="6.8" r="1.2" fill="white"/><defs><radialGradient id="ig2" cx="30%" cy="107%" r="130%"><stop offset="0%" stopColor="#fdf497"/><stop offset="20%" stopColor="#fd5949"/><stop offset="45%" stopColor="#d6249f"/><stop offset="80%" stopColor="#285AEB"/></radialGradient></defs></svg>
    </a>
  ) : null
);

const HomeAboutSection = () => (
  <div className="bg-white">

    {/* ── Main two-column row ── */}
    <div className="max-w-7xl mx-auto px-5 md:px-8" style={{ minHeight: "65vh" }}>
      <div className="grid lg:grid-cols-2 gap-12 items-center" style={{ minHeight: "65vh" }}>

        {/* Left — copy */}
        <Reveal as="div" className="py-16 lg:py-0">
          {/* Eyebrow */}
          <div className="flex items-center gap-2 mb-5">
            <BrandIcon className="w-4 h-4" style={{ color: colors.primary }} />
            <span className="text-xs font-black uppercase tracking-widest" style={{ color: colors.primary }}>
              About Us
            </span>
          </div>

          {/* Headline */}
          <h2
            className="font-display font-black leading-[1.06] mb-5"
            style={{ fontSize: "clamp(2rem,4.5vw,3.5rem)", color: colors.dark }}
          >
            {about.headlineMain}{" "}
            <span style={{ color: colors.primary }}>{about.headlineHighlight}</span>{" "}
            {about.headlineEnd}
          </h2>

          {/* Green rule */}
          <div className="w-12 h-1 rounded-full mb-7" style={{ backgroundColor: colors.primary }} />

          {/* Body */}
          <p className="text-gray-600 leading-relaxed mb-4 text-base">{about.body}</p>
          {about.body2 && (
            <p className="text-gray-600 leading-relaxed mb-8 text-base">{about.body2}</p>
          )}

          {/* Social + rating row */}
          <div className="flex items-center gap-5 flex-wrap">
            <div className="flex items-center gap-2">
              <GoogleBadge />
              <FacebookBadge />
              <InstagramBadge />
            </div>

            <div className="w-px h-10 bg-gray-200" />

            <div>
              <div className="flex gap-0.5 mb-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-sm font-black" style={{ color: colors.dark }}>5.0 Star Rating</p>
              <p className="text-xs text-gray-500">Based on Google Reviews</p>
            </div>
          </div>
        </Reveal>

        {/* Right — photo with overlay card */}
        <Reveal as="div" animation="right" className="relative h-[55vh] lg:h-full min-h-[420px]">
          <div className="absolute inset-0 rounded-2xl overflow-hidden">
            <img
              src={about.image}
              alt="About us"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Overlay card */}
          {about.overlayCard && (() => {
            const Icon = ICON_MAP[about.overlayCard.icon] ?? Users;
            return (
              <div
                className="absolute bottom-6 left-6 right-6 sm:right-auto sm:max-w-[260px] rounded-2xl p-5 flex items-start gap-4 shadow-xl"
                style={{ backgroundColor: colors.primary }}
              >
                <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-display font-black text-white text-base leading-tight mb-1">
                    {about.overlayCard.title}
                  </p>
                  <p className="text-white/80 text-sm leading-relaxed">
                    {about.overlayCard.sub}
                  </p>
                </div>
              </div>
            );
          })()}
        </Reveal>

      </div>
    </div>

    {/* ── Values row ── */}
    <div className="border-t border-gray-100" style={{ backgroundColor: siteConfig.colors.light }}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-gray-200">
          {(about.values ?? []).map(({ icon, title, body }, i) => {
            const Icon = ICON_MAP[icon] ?? Award;
            return (
              <Reveal
                key={title}
                delay={i * 100}
                className="flex flex-col items-center text-center px-8 py-8 sm:py-0 first:pl-0 last:pr-0"
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
                  style={{ backgroundColor: `${colors.primary}15` }}
                >
                  <Icon className="w-7 h-7" style={{ color: colors.primary }} />
                </div>
                <h3 className="font-display font-black text-lg mb-2" style={{ color: colors.dark }}>
                  {title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">{body}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </div>

  </div>
);

export default HomeAboutSection;
